// libs
import React, { memo, useState } from "react";

// global components
import ResponsiveLoader from "shared/components/ResponsiveLoader";
import Button from "shared/components/Button";

// components
import Title from "shared/components/AuthModal/components/Title";
import InputLabel from "shared/components/AuthModal/components/InputLabel";
import Password from "shared/components/AuthModal/components/Password";
import Terms from "shared/components/AuthModal/components/Terms";
import Error from "shared/components/AuthModal/components/Error";
import SignUpPolicy from "shared/components/AuthModal/components/SignUpPolicy";
import Input from "shared/components/AuthModal/components/Input";

// contexts
import { useModalContext } from "shared/components/Modal";

// helpers
import { validateEmail, validatePassword, validateName } from "shared/components/AuthModal/helpers";

// api
import { register } from "shared/api/auth";

// analytics
import * as Analytics from "shared/components/Header/analytics";

// constants
import { TEXTS } from "shared/components/AuthModal/constants";
import { BREAKPOINTS } from "shared/styles/base";

// styled components
import { Container } from "./styles";

// defs
import { ISignupResponse, SIGNUP_ERROR_CODE } from "./types";

interface ISignUpProps {
    formKey: string;
    updateAppUser: (user: any) => void;
    onGDPRNetwork: boolean;
}

function SignUp({ formKey, updateAppUser, onGDPRNetwork }: ISignUpProps) {
    const [error, setError] = useState("");
    const [email, setEmail] = useState("");
    const [isEmailError, setEmailError] = useState(false);
    const [name, setName] = useState("");
    const [isNameError, setNameError] = useState(false);
    const [password, setPassword] = useState("");
    const [isPasswordError, setPasswordError] = useState(false);
    const [inProgress, setProgress] = useState(false);
    const [newsletterSelected, setNewsletterSelected] = useState(false);

    const { hide } = useModalContext();

    const onPasswordChange: React.ChangeEventHandler<HTMLInputElement> = e =>
        setPassword(e.target.value);

    const onEmailChange: React.ChangeEventHandler<HTMLInputElement> = e => setEmail(e.target.value);

    const onNameChange: React.ChangeEventHandler<HTMLInputElement> = e => setName(e.target.value);

    const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (inProgress) {
            return;
        }
        Analytics.onRegisterClick();

        setEmailError(false);
        setPasswordError(false);
        setNameError(false);

        const { status: nameStatus, error: nameError } = validateName(name);
        if (nameStatus === false) {
            // @ts-ignore
            setError(nameError);
            setNameError(true);
            return;
        }

        const { status: emailStatus, error: emailError } = validateEmail(email);
        if (emailStatus === false) {
            // @ts-ignore
            setError(emailError);
            setEmailError(true);
            return;
        }

        const { status: passwordStatus, error: passwordError } = validatePassword(password);
        if (passwordStatus === false) {
            // @ts-ignore
            setError(passwordError);
            setPasswordError(true);
            return;
        }

        setError("");
        setProgress(true);

        let [firstName, lastName] = name.trim().split(" ");

        if (lastName === undefined) {
            lastName = firstName;
        }

        try {
            const offer_subscription = onGDPRNetwork && newsletterSelected ? 1 : 0;
            const response: ISignupResponse = await register({
                userName: name,
                firstName,
                lastName,
                email,
                password,
                formKey,
                offer_subscription,
            });

            if (response.error === SIGNUP_ERROR_CODE.ERROR) {
                setProgress(false);
                setError(response.message || TEXTS.OOPS);
                Analytics.onRegisterFailed();
                return;
            }

            const { form_key, cartCount, customerData } = response.response;
            Analytics.onRegisterSuccess(customerData.id);
            updateAppUser({ formKey: form_key, cartCount, customerData });
            hide();
        } catch (err) {
            setProgress(false);
            setError(TEXTS.OOPS);
        }
    };

    return (
        <Container data-cy="signup-form" onSubmit={onFormSubmit}>
            <ResponsiveLoader breakpoint={BREAKPOINTS.MOBILE} up>
                <Title>{TEXTS.CREATE_ACCOUNT}</Title>
            </ResponsiveLoader>

            <InputLabel>{TEXTS.FULL_NAME}</InputLabel>
            <Input
                className="input"
                onChange={onNameChange}
                data-cy="signup-fullname"
                hasError={isNameError}
            />
            <InputLabel>{TEXTS.EMAIL}</InputLabel>
            <Input
                className="input"
                onChange={onEmailChange}
                data-cy="signup-email"
                hasError={isEmailError}
            />
            <InputLabel>{TEXTS.PASSWORD}</InputLabel>
            <Password
                onChange={onPasswordChange}
                dataCy="signup-password"
                hasError={isPasswordError}
                className="input"
            />

            {onGDPRNetwork && (
                <SignUpPolicy
                    data-test="signup-policy"
                    onNewsletterChecked={setNewsletterSelected}
                />
            )}

            {error.length > 0 && <Error data-test="fp-error">{error}</Error>}

            <Button inProgress={inProgress} data-cy="signup-btn">
                {TEXTS.REGISTER}
            </Button>
            <Terms />
        </Container>
    );
}

export default memo(SignUp);
